import {
  useBookDemoAction,
  useCloseDemoInvitationAction,
} from '@/hooks/actions/use-demo-invitation-actions';
import {
  browserTimeZone,
  useDemoAvailabilityQuery,
} from '@/hooks/queries/use-demo-invitation-query';
import { productAnalytics } from '@/libs/product-analytics';
import { ErrorCode, type DemoInvitationDto } from '@fluenceur/types';
import { isAxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { featuredSlots, slotLabels } from './demo-slots';

/**
 * Everything the capsule and the banner share: the slots, the one picked, the
 * booking and the closing. `readSlots` holds the calendar read back until the
 * slots are actually on screen.
 */
export function useDemoBookingFlow({
  brandId,
  invitation,
  readSlots,
}: {
  brandId: string;
  invitation: DemoInvitationDto;
  readSlots: boolean;
}) {
  const { t } = useTranslation();
  const locale = t('common.locale');
  const timeZone = browserTimeZone();
  const availability = useDemoAvailabilityQuery(readSlots && !invitation.booking);
  const book = useBookDemoAction(brandId);
  const close = useCloseDemoInvitationAction(brandId);
  const [picked, setPicked] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    productAnalytics.captureForCurrentConsent()('feature_attempted', {
      feature: 'demo',
      action: 'view',
    });
  }, []);

  const days = useMemo(() => availability.data?.days ?? [], [availability.data]);
  const featured = useMemo(() => featuredSlots(days), [days]);
  const allSlots = useMemo(() => days.flatMap((day) => day.slots), [days]);
  const selected = picked && allSlots.includes(picked) ? picked : (featured[0] ?? null);

  const errorCode = isAxiosError(book.error) ? book.error.response?.data?.code : undefined;
  const bookingError = book.isError
    ? errorCode === ErrorCode.E_DEMO_SLOT_UNAVAILABLE
      ? t('demoInvitation.errors.slotTaken')
      : t('demoInvitation.errors.unavailable')
    : null;

  const booking = invitation.booking;

  return {
    locale,
    timeZone,
    days,
    featured,
    selected,
    selectedLabels: selected ? slotLabels(selected, locale, timeZone) : null,
    pick: (slot: string) => {
      book.reset();
      setPicked(slot);
    },
    showAll,
    toggleShowAll: () => setShowAll((value) => !value),
    isLoadingSlots: availability.isLoading,
    slotsFailed: availability.isError,
    hasNoSlots: availability.isSuccess && allSlots.length === 0,
    book: () => {
      if (selected) book.mutate(selected);
    },
    isBooking: book.isPending,
    bookingError,
    booking,
    bookingLabels: booking ? slotLabels(booking.start, locale, timeZone) : null,
    close: () => close.mutate({ booked: !!booking }),
  };
}

export type DemoBookingFlow = ReturnType<typeof useDemoBookingFlow>;
